import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const formatNum = (n) => { if (!n) return '0'; if (n >= 1e6) return (n/1e6).toFixed(1)+'M'; if (n >= 1e3) return (n/1e3).toFixed(n>=1e4?0:1)+'K'; return String(n); };
const tierCls = (t) => t===1?'bg-amber-500/20 text-amber-400':t===2?'bg-zinc-400/20 text-zinc-300':t===3?'bg-orange-800/20 text-orange-400':'bg-zinc-800 text-zinc-600';

const TalentPerformance = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('total_impressions');
  const [sortDir, setSortDir] = useState('desc');
  const [benchTier, setBenchTier] = useState('all');

  useEffect(() => {
    api.get('/talent-performance').then(r => setData(r.data.performers || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const sorted = [...data].sort((a, b) => {
    const av = parseFloat(a[sortBy]) || 0;
    const bv = parseFloat(b[sortBy]) || 0;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  const SortHeader = ({ field, label, className }) => (
    <th onClick={() => toggleSort(field)} className={`pb-2 cursor-pointer hover:text-white select-none ${className || ''}`}>
      {label} {sortBy === field && <span className="text-nextide-turquoise">{sortDir === 'desc' ? '↓' : '↑'}</span>}
    </th>
  );

  if (loading) return <div className="p-8 text-zinc-500">Loading...</div>;

  return (
    <div className="p-8 min-w-0 overflow-y-auto">
      <div className="flex items-center gap-4 mb-1">
        <Link to="/talent-metrics" className="text-zinc-400 hover:text-white text-sm">← Back to Metrics</Link>
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">Campaign Performance</h1>
      <p className="text-zinc-500 text-sm mb-6">{data.length} creators with campaign data</p>

      {data.length === 0 ? (
        <div className="text-zinc-500 text-center py-16">No campaign data yet. Finalize reporting periods to populate performance data.</div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="text-xs text-zinc-500 border-b border-zinc-800">
                <th className="text-left pb-2 pl-3 w-8">#</th>
                <SortHeader field="name" label="Creator" className="text-left" />
                <th className="text-left pb-2 w-10">Tier</th>
                <SortHeader field="twitch_avg_ccv" label="CCV" className="text-right" />
                <th className="text-left pb-2">Category</th>
                <SortHeader field="campaign_count" label="Campaigns" className="text-right" />
                <SortHeader field="total_impressions" label="Total Impr." className="text-right" />
                <SortHeader field="avg_impressions" label="Avg Impr." className="text-right" />
                <SortHeader field="avg_er" label="Avg ER" className="text-right" />
                <SortHeader field="avg_ctr" label="Avg CTR" className="text-right pr-3" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((t, i) => {
                const erMult = parseFloat(t.er_multiplier) || 0;
                const ctrTierAvg = parseFloat(t.tier_avg_ctr) || 0;
                const creatorCtr = parseFloat(t.avg_ctr) || 0;
                const ctrMult = ctrTierAvg > 0 ? creatorCtr / ctrTierAvg : 0;
                return (
                  <tr key={t.id} className="text-xs border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="py-2.5 pl-3 text-zinc-600">{i+1}</td>
                    <td className="py-2.5"><Link to={`/talent/${t.id}`} className="text-white text-sm font-medium hover:text-nextide-turquoise">{t.name}</Link></td>
                    <td className="py-2.5"><span className={`px-1.5 py-0.5 rounded font-semibold ${tierCls(t.talent_tier)}`}>T{t.talent_tier || '-'}</span></td>
                    <td className="py-2.5 text-purple-400 text-right">{t.twitch_avg_ccv > 0 ? formatNum(parseInt(t.twitch_avg_ccv)) : '-'}</td>
                    <td className="py-2.5 text-zinc-400 truncate max-w-[140px]">{t.primary_category || '-'}</td>
                    <td className="py-2.5 text-white text-right">{t.campaign_count}</td>
                    <td className="py-2.5 text-nextide-turquoise text-right font-medium">{formatNum(Math.round(parseFloat(t.total_impressions)))}</td>
                    <td className="py-2.5 text-zinc-300 text-right">{formatNum(Math.round(parseFloat(t.avg_impressions)))}</td>
                    <td className="py-2.5 text-right">
                      {t.avg_er ? (<>
                        <span className={erMult >= 1 ? 'text-green-400' : erMult > 0 ? 'text-red-400' : 'text-zinc-300'}>{parseFloat(t.avg_er).toFixed(2)}%</span>
                        {erMult > 0 && <span className={`text-xs ml-1 ${erMult >= 1 ? 'text-green-600' : 'text-red-600'}`}>({erMult.toFixed(1)}x)</span>}
                      </>) : '-'}
                    </td>
                    <td className="py-2.5 text-right pr-3">
                      {t.avg_ctr ? (<>
                        <span className={ctrMult >= 1 ? 'text-green-400' : ctrMult > 0 ? 'text-red-400' : 'text-zinc-300'}>{parseFloat(t.avg_ctr).toFixed(2)}%</span>
                        {ctrMult > 0 && <span className={`text-xs ml-1 ${ctrMult >= 1 ? 'text-green-600' : 'text-red-600'}`}>({ctrMult.toFixed(1)}x)</span>}
                      </>) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Benchmark Calculator */}
      {data.length > 0 && (() => {
        const tiers = [...new Set(data.filter(d => d.talent_tier).map(d => d.talent_tier))].sort();
        const filtered = benchTier === 'all' ? data : data.filter(d => String(d.talent_tier) === benchTier);
        const creatorsWithER = filtered.filter(d => parseFloat(d.avg_er) > 0);
        const creatorsWithCTR = filtered.filter(d => parseFloat(d.avg_ctr) > 0);
        const benchER = creatorsWithER.length > 0 ? creatorsWithER.reduce((s, d) => s + parseFloat(d.avg_er), 0) / creatorsWithER.length : 0;
        const benchCTR = creatorsWithCTR.length > 0 ? creatorsWithCTR.reduce((s, d) => s + parseFloat(d.avg_ctr), 0) / creatorsWithCTR.length : 0;
        const totalImpr = filtered.reduce((s, d) => s + (parseInt(d.total_impressions) || 0), 0);
        const totalCampaigns = [...new Set(filtered.flatMap(d => [d.campaign_count]))].reduce((s, c) => s + parseInt(c), 0);

        return (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-sm">Benchmark Calculator</h3>
              <div className="flex gap-1">
                <button onClick={() => setBenchTier('all')}
                  className={`px-3 py-1 rounded text-xs ${benchTier === 'all' ? 'bg-nextide-turquoise text-black font-semibold' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>All Tiers</button>
                {tiers.map(t => (
                  <button key={t} onClick={() => setBenchTier(String(t))}
                    className={`px-3 py-1 rounded text-xs ${benchTier === String(t) ? 'bg-nextide-turquoise text-black font-semibold' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>T{t}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-zinc-500 text-xs mb-1">Benchmark ER</div>
                <div className="text-3xl font-bold text-nextide-turquoise">{benchER > 0 ? benchER.toFixed(2) + '%' : '-'}</div>
                <div className="text-zinc-600 text-xs mt-1">{creatorsWithER.length} creator{creatorsWithER.length !== 1 ? 's' : ''}</div>
              </div>
              <div className="text-center">
                <div className="text-zinc-500 text-xs mb-1">Benchmark CTR</div>
                <div className="text-3xl font-bold text-nextide-turquoise">{benchCTR > 0 ? benchCTR.toFixed(2) + '%' : '-'}</div>
                <div className="text-zinc-600 text-xs mt-1">{creatorsWithCTR.length} creator{creatorsWithCTR.length !== 1 ? 's' : ''}</div>
              </div>
              <div className="text-center">
                <div className="text-zinc-500 text-xs mb-1">Total Impressions</div>
                <div className="text-3xl font-bold text-white">{formatNum(totalImpr)}</div>
                <div className="text-zinc-600 text-xs mt-1">{filtered.length} creator{filtered.length !== 1 ? 's' : ''}</div>
              </div>
              <div className="text-center">
                <div className="text-zinc-500 text-xs mb-1">Creators</div>
                <div className="text-3xl font-bold text-white">{filtered.length}</div>
                <div className="text-zinc-600 text-xs mt-1">{benchTier === 'all' ? 'All tiers' : `Tier ${benchTier}`}</div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default TalentPerformance;
